import {
	CodeBlock,
	Endpoint,
	Alert,
	Table,
	ParamList,
	Param,
	Card,
	CardGrid,
} from '../../components/docs';
import styles from '../../components/docs/docs.module.css';

export default function WalletModes() {
	const comparisonColumns = [
		{ key: 'aspect', header: 'Aspect' },
		{ key: 'integrated', header: 'Integrated Mode' },
		{ key: 'seamless', header: 'Seamless Mode' },
	];

	const comparisonData = [
		{
			aspect: 'Balance Storage',
			integrated: 'Stored by Ringfort',
			seamless: 'Stored by your system',
		},
		{
			aspect: 'Deposits',
			integrated: 'Via Ringfort deposit API',
			seamless: 'Your platform handles',
		},
		{
			aspect: 'Withdrawals',
			integrated: 'Via Ringfort withdrawal API',
			seamless: 'Your platform handles',
		},
		{
			aspect: 'Balance Updates',
			integrated: 'Automatic',
			seamless: 'Real-time callbacks',
		},
		{
			aspect: 'Implementation Time',
			integrated: '1-2 days',
			seamless: '3-5 days',
		},
		{
			aspect: 'Server Requirements',
			integrated: 'Minimal',
			seamless: 'Callback endpoints required',
		},
		{
			aspect: 'Multi-currency',
			integrated: 'Supported',
			seamless: 'Full control',
		},
		{
			aspect: 'Transaction History',
			integrated: 'Via dashboard',
			seamless: 'In your system',
		},
	];

	const callbackErrorColumns = [
		{ key: 'code', header: 'Error Code' },
		{ key: 'description', header: 'Description' },
		{ key: 'action', header: 'Ringfort Action' },
	];

	const callbackErrorData = [
		{
			code: 'INSUFFICIENT_BALANCE',
			description: 'Player does not have enough funds',
			action: 'Bet rejected, player notified',
		},
		{
			code: 'PLAYER_NOT_FOUND',
			description: 'Player ID not recognized',
			action: 'Session terminated',
		},
		{
			code: 'TRANSACTION_FAILED',
			description: 'Generic transaction failure',
			action: 'Bet rejected, retry allowed',
		},
		{
			code: 'DUPLICATE_TRANSACTION',
			description: 'Transaction ID already processed',
			action: 'Original result used',
		},
		{
			code: 'ACCOUNT_BLOCKED',
			description: 'Player account is blocked',
			action: 'Session terminated',
		},
	];

	return (
		<div className={styles.docsPage}>
			<h1>Wallet Modes</h1>
			<p className={styles.lead}>
				Ringfort Studio supports two wallet integration modes: Integrated mode
				where Ringfort manages player balances, and Seamless mode where your
				platform maintains control of all wallet operations.
			</p>

			<h2>Comparison</h2>
			<Table columns={comparisonColumns} data={comparisonData} />

			<h2>Integrated Mode</h2>
			<p>
				In integrated mode, Ringfort Studio manages player balances internally.
				Your platform deposits funds to a player's Ringfort account, and players
				can bet using those funds. When players want to cash out, you initiate a
				withdrawal.
			</p>

			<h3>When to Use Integrated Mode</h3>
			<ul>
				<li>You are building a new platform without an existing wallet system</li>
				<li>You want the fastest integration path</li>
				<li>You prefer to delegate balance management</li>
				<li>Your platform has a virtual currency system</li>
			</ul>

			<h3>Setting Up Integrated Mode</h3>
			<CodeBlock language="typescript" title="Create Integrated Session">
				{`const createIntegratedSession = async (playerId: string) => {
  const response = await fetch('https://api.rngfort.com/api/v1/operators/sessions', {
    method: 'POST',
    headers: {
      'Authorization': 'Bearer sk_live_xxxxxxxxxxxxx',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      playerId,
      currency: 'USD',
      walletMode: 'integrated', // Use integrated mode
      returnUrl: 'https://yoursite.com/casino',
    }),
  });

  return response.json();
};`}
			</CodeBlock>

			<h3>Depositing Funds</h3>
			<Endpoint
				method="POST"
				path="/api/v1/operators/players/:playerId/deposit"
				description="Deposit funds to a player's Ringfort account"
			/>
			<ParamList>
				<Param name="amount" type="string" required>
					Amount to deposit in smallest currency unit (cents for USD, satoshis
					for BTC). Must be a positive integer string.
				</Param>
				<Param name="currency" type="string" required>
					Currency code matching the player's account currency.
				</Param>
				<Param name="reference" type="string">
					Your internal reference ID for this deposit. Useful for
					reconciliation.
				</Param>
			</ParamList>
			<CodeBlock language="typescript" title="Deposit Funds">
				{`const depositFunds = async (playerId: string, amountCents: number) => {
  // First, deduct from your platform's wallet
  await yourWallet.debit(playerId, amountCents);

  // Then deposit to Ringfort
  const response = await fetch(
    \`https://api.rngfort.com/api/v1/operators/players/\${playerId}/deposit\`,
    {
      method: 'POST',
      headers: {
        'Authorization': 'Bearer sk_live_xxxxxxxxxxxxx',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        amount: String(amountCents), // "5000" for $50.00
        currency: 'USD',
        reference: \`dep_\${Date.now()}\`,
      }),
    }
  );

  const data = await response.json();

  if (data.success) {
    console.log('New balance:', data.data.balance);
    return data.data;
  }

  // Rollback your wallet if deposit failed
  await yourWallet.credit(playerId, amountCents);
  throw new Error(data.error.message);
};`}
			</CodeBlock>

			<h3>Withdrawing Funds</h3>
			<Endpoint
				method="POST"
				path="/api/v1/operators/players/:playerId/withdraw"
				description="Withdraw funds from a player's Ringfort account"
			/>
			<CodeBlock language="typescript" title="Withdraw Funds">
				{`const withdrawFunds = async (playerId: string, amountCents: number) => {
  const response = await fetch(
    \`https://api.rngfort.com/api/v1/operators/players/\${playerId}/withdraw\`,
    {
      method: 'POST',
      headers: {
        'Authorization': 'Bearer sk_live_xxxxxxxxxxxxx',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        amount: String(amountCents),
        currency: 'USD',
        reference: \`wth_\${Date.now()}\`,
      }),
    }
  );

  const data = await response.json();

  if (data.success) {
    // Credit your platform's wallet
    await yourWallet.credit(playerId, amountCents);
    console.log('Withdrawal complete, new balance:', data.data.balance);
    return data.data;
  }

  throw new Error(data.error.message);
};`}
			</CodeBlock>

			<h3>Checking Balance</h3>
			<Endpoint
				method="GET"
				path="/api/v1/operators/players/:playerId/balance"
				description="Get current balance for a player"
			/>
			<CodeBlock language="typescript" title="Check Balance">
				{`const getBalance = async (playerId: string) => {
  const response = await fetch(
    \`https://api.rngfort.com/api/v1/operators/players/\${playerId}/balance\`,
    {
      headers: {
        'Authorization': 'Bearer sk_live_xxxxxxxxxxxxx',
      },
    }
  );

  const data = await response.json();
  return data.data.balance; // "5000" for $50.00
};`}
			</CodeBlock>

			<Alert variant="info" title="Balance Format">
				All balance amounts are returned as strings representing the smallest
				currency unit. For USD, "5000" means $50.00. For BTC, the value is in
				satoshis.
			</Alert>

			<h2>Seamless Mode</h2>
			<p>
				In seamless mode, your platform maintains full control of player
				balances. Ringfort makes real-time callbacks to your wallet endpoints for
				every transaction (bet placement, win payout, rollback).
			</p>

			<h3>When to Use Seamless Mode</h3>
			<ul>
				<li>You have an existing wallet system</li>
				<li>You need unified balance across multiple game providers</li>
				<li>You require complete transaction control</li>
				<li>Regulatory requirements mandate wallet control</li>
			</ul>

			<h3>Setting Up Seamless Mode</h3>
			<p>
				First, configure your callback URLs in the Ringfort operator dashboard.
				Then create sessions with seamless mode enabled.
			</p>
			<CodeBlock language="typescript" title="Create Seamless Session">
				{`const createSeamlessSession = async (playerId: string) => {
  const response = await fetch('https://api.rngfort.com/api/v1/operators/sessions', {
    method: 'POST',
    headers: {
      'Authorization': 'Bearer sk_live_xxxxxxxxxxxxx',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      playerId,
      currency: 'USD',
      walletMode: 'seamless', // Use seamless mode
      returnUrl: 'https://yoursite.com/casino',
    }),
  });

  return response.json();
};`}
			</CodeBlock>

			<h3>Callback Endpoints</h3>
			<p>
				You must implement the following callback endpoints. Ringfort will call
				these endpoints with signed requests during gameplay.
			</p>

			<h4>Balance Check</h4>
			<Endpoint
				method="POST"
				path="/your-wallet/balance"
				description="Called to verify player balance before game actions"
			/>
			<CodeBlock language="typescript" title="Balance Callback">
				{`// Request from Ringfort
{
  "playerId": "player_12345",
  "currency": "USD",
  "sessionId": "sess_abc123",
  "timestamp": 1704067200
}

// Your response
{
  "balance": "50000", // $500.00 in cents
  "currency": "USD"
}`}
			</CodeBlock>
			<CodeBlock language="typescript" title="Balance Handler Implementation">
				{`import express from 'express';
import crypto from 'crypto';

const app = express();
const RINGFORT_PUBLIC_KEY = process.env.RINGFORT_PUBLIC_KEY;

// Verify request signature
function verifySignature(req: express.Request): boolean {
  const signature = req.headers['x-signature'] as string;
  const timestamp = req.headers['x-timestamp'] as string;
  const payload = JSON.stringify(req.body);

  const message = \`\${timestamp}.\${payload}\`;
  const expected = crypto
    .createHmac('sha256', RINGFORT_PUBLIC_KEY)
    .update(message)
    .digest('hex');

  return crypto.timingSafeEqual(
    Buffer.from(signature),
    Buffer.from(expected)
  );
}

app.post('/wallet/balance', async (req, res) => {
  // Verify the request is from Ringfort
  if (!verifySignature(req)) {
    return res.status(401).json({ error: 'Invalid signature' });
  }

  const { playerId, currency } = req.body;

  try {
    // Get balance from your system
    const balance = await yourDatabase.getPlayerBalance(playerId, currency);

    res.json({
      balance: String(balance), // Always return as string
      currency,
    });
  } catch (error) {
    res.status(500).json({
      error: 'BALANCE_FETCH_FAILED',
      message: 'Could not retrieve balance',
    });
  }
});`}
			</CodeBlock>

			<h4>Debit (Bet Placement)</h4>
			<Endpoint
				method="POST"
				path="/your-wallet/debit"
				description="Called when a player places a bet"
			/>
			<CodeBlock language="typescript" title="Debit Callback">
				{`// Request from Ringfort
{
  "transactionId": "tx_ringfort_abc123",
  "playerId": "player_12345",
  "amount": "1000", // $10.00 bet
  "currency": "USD",
  "gameId": "dice",
  "betId": "bet_xyz789",
  "sessionId": "sess_abc123",
  "timestamp": 1704067200
}

// Your response (success)
{
  "transactionId": "your_tx_001", // Your internal transaction ID
  "balance": "49000" // New balance after debit
}

// Your response (insufficient funds)
{
  "error": "INSUFFICIENT_BALANCE",
  "message": "Player does not have enough funds"
}`}
			</CodeBlock>
			<CodeBlock language="typescript" title="Debit Handler Implementation">
				{`app.post('/wallet/debit', async (req, res) => {
  if (!verifySignature(req)) {
    return res.status(401).json({ error: 'Invalid signature' });
  }

  const {
    transactionId,
    playerId,
    amount,
    currency,
    gameId,
    betId,
    sessionId,
  } = req.body;

  try {
    // Check for duplicate transaction (idempotency)
    const existing = await yourDatabase.findTransaction(transactionId);
    if (existing) {
      return res.json({
        transactionId: existing.internalId,
        balance: existing.balanceAfter,
      });
    }

    // Get current balance
    const balance = await yourDatabase.getPlayerBalance(playerId, currency);
    const amountNum = parseInt(amount, 10);

    // Check sufficient funds
    if (balance < amountNum) {
      return res.status(400).json({
        error: 'INSUFFICIENT_BALANCE',
        message: 'Player does not have enough funds',
      });
    }

    // Perform the debit
    const result = await yourDatabase.debitPlayer({
      playerId,
      amount: amountNum,
      currency,
      reference: transactionId,
      metadata: { gameId, betId, sessionId },
    });

    res.json({
      transactionId: result.transactionId,
      balance: String(result.newBalance),
    });
  } catch (error) {
    console.error('Debit failed:', error);
    res.status(500).json({
      error: 'TRANSACTION_FAILED',
      message: 'Could not process debit',
    });
  }
});`}
			</CodeBlock>

			<h4>Credit (Win Payout)</h4>
			<Endpoint
				method="POST"
				path="/your-wallet/credit"
				description="Called when a player wins and needs to be paid"
			/>
			<CodeBlock language="typescript" title="Credit Callback">
				{`// Request from Ringfort
{
  "transactionId": "tx_ringfort_def456",
  "playerId": "player_12345",
  "amount": "1980", // $19.80 win
  "currency": "USD",
  "gameId": "dice",
  "betId": "bet_xyz789",
  "referenceTransactionId": "tx_ringfort_abc123", // Original bet transaction
  "sessionId": "sess_abc123",
  "timestamp": 1704067200
}

// Your response
{
  "transactionId": "your_tx_002",
  "balance": "50980" // New balance after credit
}`}
			</CodeBlock>
			<CodeBlock language="typescript" title="Credit Handler Implementation">
				{`app.post('/wallet/credit', async (req, res) => {
  if (!verifySignature(req)) {
    return res.status(401).json({ error: 'Invalid signature' });
  }

  const {
    transactionId,
    playerId,
    amount,
    currency,
    gameId,
    betId,
    referenceTransactionId,
    sessionId,
  } = req.body;

  try {
    // Check for duplicate transaction (idempotency)
    const existing = await yourDatabase.findTransaction(transactionId);
    if (existing) {
      return res.json({
        transactionId: existing.internalId,
        balance: existing.balanceAfter,
      });
    }

    // Verify the original bet exists
    const originalBet = await yourDatabase.findTransaction(referenceTransactionId);
    if (!originalBet) {
      return res.status(400).json({
        error: 'REFERENCE_NOT_FOUND',
        message: 'Original bet transaction not found',
      });
    }

    // Perform the credit
    const result = await yourDatabase.creditPlayer({
      playerId,
      amount: parseInt(amount, 10),
      currency,
      reference: transactionId,
      metadata: { gameId, betId, sessionId, type: 'win' },
    });

    res.json({
      transactionId: result.transactionId,
      balance: String(result.newBalance),
    });
  } catch (error) {
    console.error('Credit failed:', error);
    res.status(500).json({
      error: 'TRANSACTION_FAILED',
      message: 'Could not process credit',
    });
  }
});`}
			</CodeBlock>

			<h4>Rollback</h4>
			<Endpoint
				method="POST"
				path="/your-wallet/rollback"
				description="Called to reverse a transaction due to errors"
			/>
			<CodeBlock language="typescript" title="Rollback Callback">
				{`// Request from Ringfort
{
  "transactionId": "tx_ringfort_abc123", // Transaction to rollback
  "playerId": "player_12345",
  "sessionId": "sess_abc123",
  "reason": "game_error", // or "timeout", "manual"
  "timestamp": 1704067200
}

// Your response
{
  "success": true,
  "balance": "50000" // Balance after rollback
}`}
			</CodeBlock>
			<CodeBlock language="typescript" title="Rollback Handler Implementation">
				{`app.post('/wallet/rollback', async (req, res) => {
  if (!verifySignature(req)) {
    return res.status(401).json({ error: 'Invalid signature' });
  }

  const { transactionId, playerId, sessionId, reason } = req.body;

  try {
    // Find the original transaction
    const original = await yourDatabase.findTransaction(transactionId);

    if (!original) {
      // Transaction not found - might already be rolled back
      const balance = await yourDatabase.getPlayerBalance(playerId, 'USD');
      return res.json({
        success: true,
        balance: String(balance),
      });
    }

    if (original.rolledBack) {
      // Already rolled back - return current balance
      const balance = await yourDatabase.getPlayerBalance(playerId, 'USD');
      return res.json({
        success: true,
        balance: String(balance),
      });
    }

    // Reverse the transaction
    const result = await yourDatabase.rollbackTransaction({
      transactionId,
      playerId,
      reason,
    });

    res.json({
      success: true,
      balance: String(result.newBalance),
    });
  } catch (error) {
    console.error('Rollback failed:', error);
    res.status(500).json({
      error: 'ROLLBACK_FAILED',
      message: 'Could not process rollback',
    });
  }
});`}
			</CodeBlock>

			<Alert variant="warning" title="Idempotency is Critical">
				Your callback handlers must be idempotent. Ringfort may retry failed
				requests, and your system must handle duplicate transaction IDs
				gracefully by returning the same response.
			</Alert>

			<h3>Callback Error Codes</h3>
			<Table columns={callbackErrorColumns} data={callbackErrorData} />

			<h2>Request Signing</h2>
			<p>
				All callback requests from Ringfort include a signature for verification.
				The signature is computed using HMAC-SHA256 with your public API key.
			</p>

			<h3>Signature Headers</h3>
			<CodeBlock language="bash" title="Request Headers">
				{`X-Signature: 8a7b3c4d5e6f7a8b9c0d1e2f3a4b5c6d7e8f9a0b1c2d3e4f5a6b7c8d9e0f1a2b
X-Timestamp: 1704067200
Content-Type: application/json`}
			</CodeBlock>

			<h3>Verification Algorithm</h3>
			<CodeBlock language="typescript" title="Signature Verification">
				{`import crypto from 'crypto';

function verifyRingfortSignature(
  payload: string,
  timestamp: string,
  signature: string,
  publicKey: string
): boolean {
  // Check timestamp is recent (within 5 minutes)
  const now = Math.floor(Date.now() / 1000);
  const requestTime = parseInt(timestamp, 10);

  if (Math.abs(now - requestTime) > 300) {
    console.error('Request timestamp too old');
    return false;
  }

  // Compute expected signature
  const message = \`\${timestamp}.\${payload}\`;
  const expected = crypto
    .createHmac('sha256', publicKey)
    .update(message)
    .digest('hex');

  // Use timing-safe comparison
  try {
    return crypto.timingSafeEqual(
      Buffer.from(signature),
      Buffer.from(expected)
    );
  } catch {
    return false;
  }
}

// Express middleware
function verifyRingfortMiddleware(
  req: express.Request,
  res: express.Response,
  next: express.NextFunction
) {
  const signature = req.headers['x-signature'] as string;
  const timestamp = req.headers['x-timestamp'] as string;
  const payload = JSON.stringify(req.body);

  if (!signature || !timestamp) {
    return res.status(401).json({ error: 'Missing signature headers' });
  }

  if (!verifyRingfortSignature(payload, timestamp, signature, RINGFORT_PUBLIC_KEY)) {
    return res.status(401).json({ error: 'Invalid signature' });
  }

  next();
}

// Apply to all wallet endpoints
app.use('/wallet', verifyRingfortMiddleware);`}
			</CodeBlock>

			<h2>Balance Synchronization</h2>
			<p>
				In seamless mode, balance synchronization happens in real-time through
				callbacks. However, you should implement periodic reconciliation to catch
				any discrepancies.
			</p>

			<h3>Reconciliation Endpoint</h3>
			<Endpoint
				method="GET"
				path="/api/v1/operators/reconciliation"
				description="Get transaction summary for reconciliation"
			/>
			<CodeBlock language="typescript" title="Daily Reconciliation">
				{`const reconcileDaily = async () => {
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);

  const response = await fetch(
    \`https://api.rngfort.com/api/v1/operators/reconciliation?\` +
    \`startDate=\${yesterday.toISOString()}&endDate=\${new Date().toISOString()}\`,
    {
      headers: {
        'Authorization': 'Bearer sk_live_xxxxxxxxxxxxx',
      },
    }
  );

  const ringfortData = await response.json();

  // Compare with your records
  for (const player of ringfortData.data.players) {
    const ourRecords = await yourDatabase.getPlayerTransactions(
      player.playerId,
      yesterday,
      new Date()
    );

    const ourTotal = ourRecords.reduce(
      (sum, tx) => sum + (tx.type === 'debit' ? -tx.amount : tx.amount),
      0
    );

    if (ourTotal !== player.netAmount) {
      console.error(\`Discrepancy for player \${player.playerId}:\`, {
        ringfort: player.netAmount,
        ours: ourTotal,
      });
      // Alert operations team
      await alertOperations(player.playerId, ourTotal, player.netAmount);
    }
  }
};

// Run daily at midnight
cron.schedule('0 0 * * *', reconcileDaily);`}
			</CodeBlock>

			<h2>Testing Wallet Integration</h2>
			<p>
				Use sandbox mode to test your wallet integration without real money.
			</p>
			<CodeBlock language="typescript" title="Test Wallet Callbacks">
				{`// Ringfort provides a testing endpoint to simulate callbacks
const testWalletCallback = async () => {
  const response = await fetch(
    'https://api.rngfort.com/api/v1/operators/test/wallet-callback',
    {
      method: 'POST',
      headers: {
        'Authorization': 'Bearer sk_test_xxxxxxxxxxxxx',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        type: 'debit', // or 'credit', 'balance', 'rollback'
        playerId: 'test_player_123',
        amount: '1000',
      }),
    }
  );

  const result = await response.json();
  console.log('Callback test result:', result);
  // Shows if your endpoint responded correctly
};`}
			</CodeBlock>

			<div className={styles.nextSteps}>
				<h2>Next Steps</h2>
				<CardGrid columns={3}>
					<Card
						title="Webhooks"
						description="Receive real-time event notifications"
						href="/docs/webhooks"
					/>
					<Card
						title="Testing Guide"
						description="Test your integration thoroughly"
						href="/docs/testing-guide"
					/>
					<Card
						title="Error Codes"
						description="Handle errors gracefully"
						href="/docs/errors"
					/>
				</CardGrid>
			</div>
		</div>
	);
}
